/**
 * Readiness is derived and typed, never stored (decision 0035 §1.5). First match wins. The scheduler's triggers are
 * `newlyReady` (after a commit) and `readyItems` (the alarm). `with_person` is never dispatched; it drives My work.
 */

import { askerWhoLeft } from "./multiplayer"
import { workerLimitOf } from "./policy"
import { agentOf, isOpenProposal, itemOf, liveWorkerRuns, must, runOf } from "./state"
import { ancestorsOf, childCounts, childrenOf, isContainer, isPlannable } from "./tree"
import type {
  Agent,
  AgentId,
  Item,
  ItemId,
  NodeRef,
  ProjectState,
  ProposalId,
  Readiness,
  UserId,
} from "./types"
import { ownerLeft } from "./workers"

const MAX_HOPS = 5

/**
 * The item whose doneness counts for a predecessor: a duplicate or superseded item follows its pointer (≤ 5 hops).
 * undefined = the item no longer exists.
 */
export const resolvePredecessor = (s: ProjectState, id: ItemId): Item | undefined => {
  let cur = itemOf(s, id)
  for (let i = 0; i < MAX_HOPS && cur?.status === "cancelled"; i++) {
    const r = cur.resolution
    if (r?._tag === "duplicate") cur = itemOf(s, r.of)
    else if (r?._tag === "superseded") cur = itemOf(s, r.by)
    else break
  }
  return cur
}

export interface Predecessors {
  /** Open (or missing) predecessors that hold it. */
  readonly on: ReadonlyArray<NodeRef>
  /** Cancelled predecessors (won't do, impossible): they keep holding (a cancelled QA never releases Submit, G5). */
  readonly cancelled: ReadonlyArray<ItemId>
}

/** Unmet `blocks` predecessors of an item: edges into it or into any item above it. */
export const blockingPredecessors = (s: ProjectState, item: Item): Predecessors => {
  const gated = new Set<string>([item.id, ...ancestorsOf(s, item).map((a) => a.id)])
  const on: NodeRef[] = []
  const cancelled: ItemId[] = []
  for (const e of s.edges) {
    if (e.kind !== "blocks" || !gated.has(e.to.id)) continue
    const p = resolvePredecessor(s, e.from.id)
    if (p?.status === "done") continue
    if (p?.status === "cancelled") {
      if (!cancelled.includes(p.id)) cancelled.push(p.id)
      continue
    }
    const id = p?.id ?? e.from.id
    if (!on.some((o) => o.id === id)) on.push({ _tag: "item", id })
  }
  return { on, cancelled }
}

/** Open proposals whose `blocks` name the item or an item above it (G2). */
export const blockingProposals = (s: ProjectState, item: Item): ReadonlyArray<ProposalId> => {
  const gated = new Set<string>([item.id, ...ancestorsOf(s, item).map((a) => a.id)])
  return s.proposals
    .filter(
      (p) =>
        isOpenProposal(p) &&
        !(p.body._tag === "outward_action" && p.state === "approved") &&
        p.blocks.some((b) => b._tag === "item" && gated.has(b.id)),
    )
    .map((p) => p.id)
}

/** The open occurrence of a series item (its open child), if any: one at a time. */
export const openOccurrenceOf = (s: ProjectState, series: Pick<Item, "id">): Item | undefined =>
  childrenOf(s, series.id).find((c) => c.status !== "done" && c.status !== "cancelled")

/**
 * Rule 8: a leaf runs when the top-level item above it is Now, or it is a non-plannable top-level item itself. An item
 * above in triage, backlog, blocked, closed or paused holds everything under it.
 */
const notNow = (s: ProjectState, t: Item): Extract<Readiness, { _tag: "not_now" }> | null => {
  const up = ancestorsOf(s, t)
  for (const a of up) {
    const why =
      a.status === "triage"
        ? "ancestor_triage"
        : a.status === "backlog"
          ? "ancestor_backlog"
          : a.status === "blocked"
            ? "ancestor_blocked"
            : a.status === "done" || a.status === "cancelled"
              ? "ancestor_closed"
              : a.paused
                ? "ancestor_paused"
                : null
    if (why) return { _tag: "not_now", itemId: a.id, why }
  }
  const top = up[up.length - 1]
  if (top && top.status !== "in_progress") return { _tag: "not_now", itemId: top.id, why: "top_not_now" }
  return null
}

export const readiness = (s: ProjectState, itemId: ItemId, now: number): Readiness => {
  const t = itemOf(s, itemId)
  if (!t) return { _tag: "not_applicable", status: "cancelled" }
  // 1. lifecycle states that are not schedulable
  if (t.status !== "todo" && t.status !== "in_progress") return { _tag: "not_applicable", status: t.status }
  // 1a. a series is never dispatched itself (Run now creates an occurrence)
  if (t.recurrence !== null) return { _tag: "recurring", openOccurrence: openOccurrenceOf(s, t)?.id ?? null }
  // 2. an attempt is live: "waiting for runner" shows here, with its reasons
  if (t.activeRunId !== null) {
    const run = runOf(s, t.activeRunId)
    const st = run?.state
    const runId = must(run?.id ?? t.activeRunId, "active run")
    if (st?._tag === "waiting") return { _tag: "running", runId, state: "waiting", unmet: st.unmet }
    if (st?._tag === "leased") return { _tag: "running", runId, state: "leased", unmet: [] }
    return { _tag: "running", runId, state: "requested", unmet: [] }
  }
  // 3. K8: a paused or archived project starts no new attempts
  const status = s.project?.status
  if (status === "paused" || status === "archived") return { _tag: "project_inactive", status }
  // 4. a hold
  if (t.paused) return { _tag: "paused" }
  // 5. containers: their children carry the work; the owner closes them
  if (isContainer(s, t)) {
    const c = childCounts(s, t.id)
    return c.open > 0
      ? { _tag: "has_open_children", open: c.open, total: c.total }
      : { _tag: "ready_to_close" }
  }
  // 5a. a plannable item with no open work under it: never dispatched, never "no owner"
  if (isPlannable(t)) return { _tag: "empty" }
  // 6. owner and assignee
  const owner = t.owner
  if (owner === null) return { _tag: "unassigned", missing: "owner" }
  if (owner._tag === "agent") {
    const a = agentOf(s, owner.agentId)
    if (!a || a.status === "retired") return { _tag: "unassigned", missing: "owner" }
  }
  const assignee = t.assignee
  if (assignee === null || assignee._tag === "agent") return { _tag: "unassigned", missing: "assignee" }
  let ownerAgent: Agent | undefined
  if (assignee._tag === "worker") {
    // a new worker needs an agent owner, which supervises it
    if (owner._tag !== "agent") return { _tag: "unassigned", missing: "owner" }
    ownerAgent = must(agentOf(s, owner.agentId), "owner agent")
    if (ownerAgent.status === "paused") return { _tag: "owner_paused", agentId: ownerAgent.id }
    // K10: an agent whose owner left starts no runs until someone takes it over
    if (ownerLeft(s, ownerAgent)) return { _tag: "owner_left", agentId: ownerAgent.id }
    // 7. checks: no worker starts a leaf without one
    if (t.checks.length === 0) return { _tag: "needs_checks" }
  }
  // 8. the top-level item above is Now; nothing above parks it
  const nn = notNow(s, t)
  if (nn) return nn
  // 9. cross-area request
  if (t.request && t.request.state !== "accepted") return { _tag: "waiting_request" }
  // 10. not before
  if (t.notBefore !== null && t.notBefore > now) return { _tag: "waiting_until", at: t.notBefore }
  // 11. blocks predecessors (of it or an item above)
  const deps = blockingPredecessors(s, t)
  if (deps.on.length > 0 || deps.cancelled.length > 0)
    return { _tag: "waiting_deps", on: deps.on, cancelled: deps.cancelled }
  // 12. decisions (G2: a question on an item above holds it too)
  const proposalIds = blockingProposals(s, t)
  if (proposalIds.length > 0) return { _tag: "waiting_decision", proposalIds }
  // 15. a person's leaf: their turn
  if (assignee._tag === "user") return { _tag: "with_person", userId: assignee.userId }
  // 13. retry backoff
  if (t.retry && t.retry.at > now) return { _tag: "retry_backoff", until: t.retry.at }
  // 14. the owner agent's worker limit
  const agent = must(ownerAgent, "owner agent")
  const limit = workerLimitOf(s, agent)
  if (limit !== null && liveWorkerRuns(s, agent.id).length >= limit)
    return { _tag: "at_capacity", agentId: agent.id, limit }
  // 16.
  return { _tag: "ready" }
}

export const isReady = (s: ProjectState, itemId: ItemId, now: number): boolean =>
  readiness(s, itemId, now)._tag === "ready"

/**
 * Ready, and someone to run it as (wave E, decision 0063, gap 7): an item whose asker left the workspace is skipped by
 * the scheduler, so decide never has to refuse it on every pass. It waits until a person edits or retries it (which
 * makes them the asker). Derived; nothing is written.
 */
export const isDispatchable = (s: ProjectState, itemId: ItemId, now: number): boolean => {
  if (!isReady(s, itemId, now)) return false
  const t = itemOf(s, itemId)
  return t !== undefined && askerWhoLeft(s, t) === null
}

const byRank = (a: Item, b: Item): number =>
  a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0

/** Worker leaves whose readiness became `ready` between two states: the scheduler's trigger after a commit. */
export const newlyReady = (before: ProjectState, after: ProjectState, now: number): ReadonlyArray<ItemId> =>
  after.items
    .filter(
      (t) =>
        t.status === "todo" &&
        isDispatchable(after, t.id, now) &&
        !(itemOf(before, t.id) && isDispatchable(before, t.id, now)),
    )
    .map((t) => t.id)

/** Every ready worker leaf in rank order: what the alarm dispatches (a passed retry or notBefore, a cold start). */
export const readyItems = (s: ProjectState, now: number): ReadonlyArray<ItemId> =>
  s.items
    .filter((t) => t.status === "todo" && isDispatchable(s, t.id, now))
    .sort(byRank)
    .map((t) => t.id)

/** Earliest future retry.at among todo items. */
export const nextRetryAt = (s: ProjectState, now: number): number | null => {
  let min: number | null = null
  for (const t of s.items)
    if (t.status === "todo" && t.retry && t.retry.at > now && (min === null || t.retry.at < min))
      min = t.retry.at
  return min
}

// ---------------------------------------------------------------------------------------------------------------
// Waiting on and holds (the frontier of people's moves)

/** Who a frontier item waits for. */
export type FrontierWho =
  | { readonly _tag: "person"; readonly userId: UserId }
  | { readonly _tag: "worker"; readonly agentId: AgentId | null }
  | { readonly _tag: "agent"; readonly agentId: AgentId }
  | { readonly _tag: "decision"; readonly proposalId: ProposalId }
  | { readonly _tag: "cancelled" }
  | { readonly _tag: "nobody" }

export interface Frontier {
  /** The item (null for a decision, which is a proposal). */
  readonly itemId: ItemId | null
  readonly who: FrontierWho
}

/** Who does an open item that waits on nothing: its assignee, else its owner. */
export const whoOf = (_s: ProjectState, t: Item): FrontierWho => {
  const a = t.assignee
  if (a?._tag === "user") return { _tag: "person", userId: a.userId }
  if (a?._tag === "worker")
    return { _tag: "worker", agentId: t.owner?._tag === "agent" ? t.owner.agentId : null }
  if (a?._tag === "agent") return { _tag: "agent", agentId: a.agentId }
  if (t.owner?._tag === "user") return { _tag: "person", userId: t.owner.userId }
  if (t.owner?._tag === "agent") return { _tag: "agent", agentId: t.owner.agentId }
  return { _tag: "nobody" }
}

/**
 * The frontier an item waits on: follow unmet `blocks` (into it or above), open children of containers and decisions
 * to the first items that wait on nothing, each with who moves next ("waiting on Jannik · video, QA").
 */
export const waitingOn = (s: ProjectState, item: Item, _now: number): ReadonlyArray<Frontier> => {
  const out: Frontier[] = []
  const seen = new Set<string>([item.id])
  const proposals = new Set<string>()
  const gatesOf = (
    t: Item,
  ): { items: ReadonlyArray<Item | ItemId>; decisions: ReadonlyArray<ProposalId> } => {
    const deps = blockingPredecessors(s, t)
    const items: Array<Item | ItemId> = [
      ...deps.on.map((n) => itemOf(s, n.id as ItemId) ?? (n.id as ItemId)),
      ...deps.cancelled.map((id) => itemOf(s, id) ?? id),
    ]
    if (isContainer(s, t))
      for (const c of childrenOf(s, t.id)) if (c.status !== "done" && c.status !== "cancelled") items.push(c)
    return { items, decisions: blockingProposals(s, t) }
  }
  const visit = (t: Item) => {
    const g = gatesOf(t)
    for (const p of g.decisions)
      if (!proposals.has(p)) {
        proposals.add(p)
        out.push({ itemId: null, who: { _tag: "decision", proposalId: p } })
      }
    for (const x of g.items) {
      if (typeof x === "string") {
        if (!seen.has(x)) {
          seen.add(x)
          out.push({ itemId: x, who: { _tag: "nobody" } })
        }
        continue
      }
      if (seen.has(x.id)) continue
      seen.add(x.id)
      if (x.status === "cancelled") {
        out.push({ itemId: x.id, who: { _tag: "cancelled" } })
        continue
      }
      const inner = gatesOf(x)
      if (inner.items.length === 0 && inner.decisions.length === 0)
        out.push({ itemId: x.id, who: whoOf(s, x) })
      else visit(x)
    }
  }
  visit(item)
  return out
}

/** Open leaves (other than the item) whose frontier includes this item: "holds 3". */
export const holds = (s: ProjectState, item: Item, now: number): ReadonlyArray<Item> =>
  s.items.filter(
    (l) =>
      l.id !== item.id &&
      l.status !== "done" &&
      l.status !== "cancelled" &&
      !isContainer(s, l) &&
      waitingOn(s, l, now).some((f) => f.itemId === item.id),
  )

/** The person whose move an item is, if it is a person's item with every gate open. */
export const personsMove = (s: ProjectState, item: Item, now: number): UserId | null => {
  const r = readiness(s, item.id, now)
  return r._tag === "with_person" ? r.userId : null
}
